import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Alert, Button, StatusHint } from '@indipia/ui-core';
import { useAuthStore } from '@indipia/shared';
import { fetchSite, fetchSiteSchedulePeriod, fetchActiveWorklog, createWorklog, fetchHolidays } from './api';
import type { StepBResult } from './types';

interface StepDProps {
  stepBResult: StepBResult;
  /** 미전달 시 '이전' 버튼을 숨긴다 — 마법사(SiteRegistrationPopup) 밖에서 공사기간만 단독으로 받을 때 쓴다. */
  onPrev?: () => void;
  /** opts.selectedExisting=true 면 신규 등록이 아니라 기등록 현장 '확인' 선택 */
  onComplete: (siteId: number, opts?: { selectedExisting?: boolean }) => void;
  onClose: () => void;
}

// ── 날짜 유틸 (ISO 'YYYY-MM-DD' 문자열 기준 — 문자열 비교로 대소 판정 가능) ──

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

/** 년/월/일 → 'YYYY-MM-DD' */
export function toIso(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** 'YYYY-MM-DD' → { year, month } (파싱 실패 시 null) */
export function parseYm(iso: string): { year: number; month: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? { year: Number(m[1]), month: Number(m[2]) } : null;
}

/** 해당 월의 1일 요일 + 말일 */
function monthMeta(year: number, month: number) {
  return {
    firstDow: new Date(year, month - 1, 1).getDay(),
    lastDate: new Date(year, month, 0).getDate(),
  };
}

export function shiftMonth(ym: { year: number; month: number }, delta: number) {
  const next = ym.month + delta;
  if (next > 12) return { year: ym.year + 1, month: 1 };
  if (next < 1) return { year: ym.year - 1, month: 12 };
  return { year: ym.year, month: next };
}

/** 시작~종료일 중 일요일·공휴일을 제외한 근무일수 (양끝 포함) */
function countWorkdays(start: string, end: string, holidays: Set<string>): number {
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e < s) return 0;
  let n = 0;
  const d = new Date(s);
  while (d <= e) {
    const dow = d.getDay(); // 0=일요일
    if (dow !== 0 && !holidays.has(toIso(d.getFullYear(), d.getMonth() + 1, d.getDate()))) n += 1;
    d.setDate(d.getDate() + 1);
  }
  return n;
}

/**
 * 월 달력 1개 — 기획서 `.sm-cal-block` (‹ 년월 › + 7열 그리드)
 * 시작일용·종료일용으로 각각 하나씩 쓴다.
 *
 * StepD(공사기간 등록)뿐 아니라 목록 화면들의 기간 필터(예: work-log RangeCalendar)에서도
 * 그대로 재사용한다 — 달력 navigation·요일/공휴일 표시·기간 선택 UX를 화면마다 다시 구현하면
 * 어긋나기 쉬우므로, 이 컴포넌트 하나를 공용 소스로 삼는다. export해 다른 패키지에서도 쓴다.
 */
export interface MonthCalendarProps {
  ym: { year: number; month: number };
  selected: string;
  /** 선택된 공사기간 — 두 달력 모두 같은 값을 받아 사이 날짜를 음영 처리한다 */
  rangeStart: string;
  rangeEnd: string;
  /** 이 날짜보다 앞은 선택 불가 (종료일 달력에서 시작일 이전 차단) */
  minDate?: string;
  disabled?: boolean;
  onNav: (delta: number) => void;
  onPick: (iso: string) => void;
  /** 공휴일 날짜 셋 — 일요일처럼 빨간색으로 표시 */
  holidays: Set<string>;
  /**
   * 년월 네비 화살표 노출 범위 — 기본 'both'(양쪽 다, StepD 기본 동작).
   * 두 달력을 한 쌍으로 묶어 바깥쪽에만 화살표를 두고 싶을 때(예: work-log RangeCalendar처럼
   * 두 달을 이어서 보여주며 함께 넘기는 화면) 왼쪽 달력엔 'prevOnly', 오른쪽엔 'nextOnly'를 준다 —
   * 반대쪽은 자리만 차지하는 빈 칸으로 대체돼 라벨 정렬은 그대로 유지된다.
   */
  nav?: 'both' | 'prevOnly' | 'nextOnly';
  /**
   * 셀 밀도 — 기본 'sm'(StepD 공사기간 팝업 스케일, 변경 없음).
   * 'lg'는 work-log RangeCalendar(목록 화면 기간 필터)의 기존 스케일에 맞춘 여유로운 크기
   * (요일 h-7 text-caption, 날짜 h-8 w-8 text-body-sm, 월 라벨 text-body-sm).
   */
  size?: 'sm' | 'lg';
}

export function MonthCalendar({
  ym,
  selected,
  rangeStart,
  rangeEnd,
  minDate,
  disabled,
  onNav,
  onPick,
  holidays,
  nav = 'both',
  size = 'sm',
}: MonthCalendarProps) {
  const { firstDow, lastDate } = monthMeta(ym.year, ym.month);
  const todayIso = toIso(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate());
  const isLg = size === 'lg';
  const dowHeaderClass = isLg
    ? 'flex h-7 items-center justify-center text-caption font-semibold'
    : 'py-0.5 text-center text-xs font-bold';
  const monthLabelClass = isLg ? 'text-body-sm font-bold' : 'text-xs font-bold';
  const dayCellSizeClass = isLg
    ? 'flex h-8 w-8 items-center justify-center text-body-sm'
    : 'py-1 text-center text-xs';
  // 기간 사이 날짜(밴드) 굵기 — 시작·종료일 배지만 굵고(font-bold, 위 branch) 사이 날짜는 일반 굵기.
  const inRangeWeightClass = 'font-normal';

  return (
    <div className={`flex-1 ${isLg ? '' : 'px-2.5 py-3'}`}>
      {/* 년월 네비 — nav='prevOnly'/'nextOnly'면 반대쪽은 같은 크기의 빈 자리로 대체(라벨 중앙정렬 유지) */}
      <div className="mb-2 flex items-center justify-between">
        {nav !== 'nextOnly' ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onNav(-1)}
            className="rounded-sm px-1.5 py-0.5 text-base leading-none text-text-secondary hover:bg-surface-subtle disabled:opacity-40"
            aria-label="이전 달"
          >
            ‹
          </button>
        ) : (
          <span aria-hidden className="invisible px-1.5 py-0.5 text-base leading-none">‹</span>
        )}
        <span className={`${monthLabelClass} text-text-primary`}>
          {ym.year}년 {ym.month}월
        </span>
        {nav !== 'prevOnly' ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onNav(1)}
            className="rounded-sm px-1.5 py-0.5 text-base leading-none text-text-secondary hover:bg-surface-subtle disabled:opacity-40"
            aria-label="다음 달"
          >
            ›
          </button>
        ) : (
          <span aria-hidden className="invisible px-1.5 py-0.5 text-base leading-none">›</span>
        )}
      </div>

      {/* 요일 + 날짜 그리드 — 기간 음영이 끊기지 않도록 셀 간격 없음 */}
      <div className="grid grid-cols-7">
        {DAY_NAMES.map((d, i) => (
          <div
            key={d}
            className={`${dowHeaderClass} ${
              i === 0 ? 'text-danger' : i === 6 ? 'text-primary-500' : 'text-text-muted'
            }`}
          >
            {d}
          </div>
        ))}

        {Array.from({ length: firstDow }, (_, i) => <div key={`pad-${i}`} />)}

        {Array.from({ length: lastDate }, (_, i) => {
          const day = i + 1;
          const iso = toIso(ym.year, ym.month, day);
          const dow = (firstDow + i) % 7;
          const isSelected = iso === selected;
          const isToday = iso === todayIso;
          const isBlocked = !!disabled || (!!minDate && iso < minDate);
          // 시작일·종료일 사이 — 두 달력 모두에 칠해져 기간 길이가 눈에 들어온다
          const isInRange =
            !!rangeStart && !!rangeEnd && iso > rangeStart && iso < rangeEnd;
          const isEdge = iso === rangeStart || iso === rangeEnd;
          // 공휴일은 일요일처럼 빨간색으로 표시해 평일과 구분한다
          const isHoliday = holidays.has(iso);

          /**
           * 기간 전체를 하나의 이어진 "덩어리"로 — 이웃(위·아래·좌·우)이 이어져 있는 방향은 각지게(0) 두고,
           * 이웃이 없어(그 방향에서 기간이 끝나) 노출된 모서리만 둥글린다. 개별 날짜를 낱개로 둥글리지 않아
           * 여러 주에 걸쳐도 세로줄이 물결치지 않고, 이어진 날짜 사이엔 빈틈이 생기지 않는다.
           */
          const inHighlightedRange = (candidateIso: string) =>
            !!rangeStart && !!rangeEnd && candidateIso >= rangeStart && candidateIso <= rangeEnd;
          const aboveFilled = day > 7 && inHighlightedRange(toIso(ym.year, ym.month, day - 7));
          const belowFilled = day + 7 <= lastDate && inHighlightedRange(toIso(ym.year, ym.month, day + 7));
          const leftFilled = dow > 0 && day > 1 && inHighlightedRange(toIso(ym.year, ym.month, day - 1));
          const rightFilled = dow < 6 && day < lastDate && inHighlightedRange(toIso(ym.year, ym.month, day + 1));
          // 기간 사이 날짜(밴드) — 위/아래/좌/우 4모서리 중 이웃 없는 쪽만 개별로 둥글린다.
          // radius는 D-07 §11 표준 토큰의 md(8px).
          const inRangeCorners = [
            !aboveFilled && !leftFilled ? 'rounded-tl-md' : '',
            !aboveFilled && !rightFilled ? 'rounded-tr-md' : '',
            !belowFilled && !leftFilled ? 'rounded-bl-md' : '',
            !belowFilled && !rightFilled ? 'rounded-br-md' : '',
          ].filter(Boolean).join(' ');

          // 요일/공휴일 텍스트색 — 기간중(isInRange)·평시에는 선택 여부와 무관하게 항상 유지한다.
          // 단, 시작·종료일 배지(isSelected/isEdge)는 배경이 꽉 찬 원(bg-primary-500)이라 흰 글자를 유지한다 —
          // 토요일 텍스트색(text-primary-500)이 배경과 같은 토큰이라 그대로 쓰면 글자가 안 보이게 된다.
          // 'lg'(RangeCalendar)는 오늘을 색 대신 테두리로만 표시(원본 동작) — 요일색을 덮지 않는다.
          const weekdayTextClass = isBlocked
            ? 'text-text-disabled'
            : isToday && !isLg
              ? 'text-primary-600'
              : dow === 0 || isHoliday
                ? 'text-danger'
                : dow === 6
                  ? 'text-primary-500'
                  : 'text-text-primary';
          // 'lg'의 오늘 표시 — 테두리 링만(원본: border 1px solid var(--shell-border)). 레이아웃 흔들림
          // 방지를 위해 평시에도 투명 테두리를 같은 두께로 깔아둔다.
          const todayRingClass = isLg ? (isToday ? 'border border-border' : 'border border-transparent') : '';

          if ((isSelected || isEdge) && (leftFilled || rightFilled)) {
            // 같은 줄에 이어지는 밴드가 있는 시작·종료일 — 버튼 자체는 원본과 완전히 동일한 크기·모양을
            // 유지한다(absolute+inset-0로 래퍼 자리에 겹쳐 그릴 뿐, 클래스는 그대로). 래퍼(div)는
            // absolute 자식만 가져 자체 콘텐츠 높이가 없으므로 그리드 행 높이 계산에 관여하지 않는다 —
            // 행 높이는 이 셀이 아니라 같은 줄의 다른 셀들로만 결정되어 예전처럼 흔들리지 않는다.
            return (
              <div key={iso} className="relative">
                <span
                  aria-hidden
                  className={`absolute inset-y-0 w-1/2 bg-primary-50 ${leftFilled ? 'left-0' : 'right-0'}`}
                />
                <button
                  type="button"
                  disabled={isBlocked}
                  onClick={() => onPick(iso)}
                  className={`absolute inset-0 flex items-center justify-center rounded-full bg-primary-500 font-bold text-white transition ${isLg ? 'text-body-sm' : 'text-xs'}`}
                >
                  {day}
                </button>
              </div>
            );
          }

          return (
            <button
              key={iso}
              type="button"
              disabled={isBlocked}
              onClick={() => onPick(iso)}
              className={`${dayCellSizeClass} transition ${
                isSelected || isEdge
                  ? 'rounded-full bg-primary-500 font-bold text-white'
                  : isInRange
                    ? `${inRangeCorners} bg-primary-50 ${inRangeWeightClass} ${weekdayTextClass}`
                    : isBlocked
                      ? `cursor-not-allowed rounded-full ${weekdayTextClass}`
                      : `rounded-full hover:bg-surface-subtle ${todayRingClass} ${isLg || !isToday ? '' : 'font-bold'} ${weekdayTextClass}`
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Step D — 공사기간 등록 (정의서 E-3 'D')
 *
 * requiredSteps에 'D'가 포함된 에이전트에서만 노출된다.
 * 신규 등록 현장·기존 선택 현장 모두 이 단계를 거치며, 부족한 현장 정보는
 * Step C와 동일하게 siteId 기준으로 직접 조회해 채운다.
 *
 * 공사기간 UI는 기획서 `.sm-cal-wrap` 패턴 — 기간 요약 트리거 + 시작일/종료일 달력 2개 + 합계 바.
 *
 * 진입 시 조회:
 *   1. GET /customer/sites/{siteId}    — 대상 현장 표시
 *   2. GET /schedule/schedules?siteIds= — 공사기간 기본값 (있으면 자동 입력)
 *   3. GET /worklog/worklogs?siteId=   — 미완료 공사현황 중복 확인
 *
 * 저장 흐름:
 *   POST /worklog/worklogs (siteId·memberId·공사기간) → onComplete
 *   공사기간은 필수 — 건너뛰기 없음.
 */
export function StepD({ stepBResult, onPrev, onComplete }: StepDProps) {
  const { siteId } = stepBResult;
  const memberId = useAuthStore((s) => s.user?.memberId ?? null);

  const today = new Date();
  const thisYm = { year: today.getFullYear(), month: today.getMonth() + 1 };

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // 공사기간은 대개 앞으로 일어날 일 — 시작일 달력은 이번 달, 종료일 달력은 다음 달을 기본으로 연다
  const [startYm, setStartYm] = useState(thisYm);
  const [endYm, setEndYm] = useState(() => shiftMonth(thisYm, 1));
  // 스케줄 기본값은 사용자가 손대기 전 최초 1회만 채운다
  const prefilledRef = useRef(false);

  const { data: siteDetail, isLoading: isSiteLoading } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => fetchSite(siteId),
    staleTime: 60_000,
  });

  const { data: schedulePeriod, isLoading: isPeriodLoading } = useQuery({
    queryKey: ['site-schedule-period', siteId],
    queryFn: () => fetchSiteSchedulePeriod(siteId),
    staleTime: 60_000,
  });

  const { data: activeWorklog, isLoading: isWorklogChecking } = useQuery({
    queryKey: ['active-worklog', siteId],
    queryFn: () => fetchActiveWorklog(siteId),
    staleTime: 10_000,
  });

  // 공휴일 — 달력은 한 번에 두 달만 렌더하지만, 조회는 어차피 1회이므로
  // 보이는 월이 속한 '연도 전체'를 받아둔다 → 같은 해 안 월 이동(‹ ›) 시 재조회 없이 빨간날 즉시 표시.
  // 이 범위가 선택 기간을 포함하므로 총 공사일수 계산과 달력 빨간날 표시에 함께 쓴다.
  const holRange = useMemo(() => {
    const fromYear = Math.min(startYm.year, endYm.year);
    const toYear = Math.max(startYm.year, endYm.year);
    return { from: `${fromYear}-01-01`, to: `${toYear}-12-31` };
  }, [startYm.year, endYm.year]);

  const { data: holidayDates } = useQuery({
    queryKey: ['holidays', holRange.from, holRange.to],
    queryFn: () => fetchHolidays(holRange.from, holRange.to),
    staleTime: 60 * 60_000,
  });
  const holidays = useMemo(() => new Set(holidayDates ?? []), [holidayDates]);

  // 공사기간 기본값 — 공사현황(있으면) > 스케줄 순. 둘 다 없으면 사용자 입력.
  // 두 조회(스케줄·공사현황)가 모두 끝난 뒤에만 채워 우선순위가 도착순서에 흔들리지 않게 한다.
  useEffect(() => {
    if (prefilledRef.current || isWorklogChecking || isPeriodLoading) return;
    const period =
      activeWorklog?.constStartDate && activeWorklog?.constEndDate
        ? { startDate: activeWorklog.constStartDate, endDate: activeWorklog.constEndDate }
        : schedulePeriod
          ? { startDate: schedulePeriod.startDate, endDate: schedulePeriod.endDate }
          : null;
    if (!period) return;
    prefilledRef.current = true;
    setStartDate(period.startDate);
    setEndDate(period.endDate);
    const sYm = parseYm(period.startDate);
    const eYm = parseYm(period.endDate);
    if (sYm) setStartYm(sYm);
    if (eYm) setEndYm(eYm);
  }, [activeWorklog, schedulePeriod, isWorklogChecking, isPeriodLoading]);

  const createWorklogMutation = useMutation({
    mutationFn: createWorklog,
    onSuccess: () => onComplete(siteId),
    onError: (e) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '공사현황 등록에 실패했습니다.';
      toast.error(msg);
    },
  });

  const isDuplicated = !!activeWorklog;
  // 인디플래너 스케줄에 공사기간이 있으면 그 값을 그대로 쓴다 — 여기서는 수정 불가.
  // (기간 변경은 인디플래너 쪽에서 해야 두 곳의 값이 어긋나지 않는다)
  const isScheduleLocked = !!schedulePeriod;
  const isReadOnly = isDuplicated || isScheduleLocked;
  const canSave = !!startDate && !!endDate && !isDuplicated;

  /** 시작일 선택 — 기존 종료일이 시작일보다 앞이면 비운다 */
  function handlePickStart(iso: string) {
    prefilledRef.current = true;
    setStartDate(iso);
    if (endDate && endDate < iso) setEndDate('');
    // 종료일 달력이 시작월보다 과거를 보고 있으면 시작월 다음 달로 당겨온다
    const picked = parseYm(iso);
    if (picked) {
      setEndYm((ym) =>
        ym.year * 12 + ym.month < picked.year * 12 + picked.month
          ? shiftMonth(picked, 1)
          : ym,
      );
    }
  }

  function handlePickEnd(iso: string) {
    prefilledRef.current = true;
    setEndDate(iso);
  }

  function handleSave() {
    if (memberId == null) {
      toast.error('로그인 정보를 확인할 수 없습니다. 다시 로그인해 주세요.');
      return;
    }
    // 저장은 공사현황이 없을 때만 가능(canSave). 스케줄에서 채운 경우 scheduleId를 함께 저장한다.
    createWorklogMutation.mutate({
      siteId,
      memberId,
      scheduleId: schedulePeriod?.scheduleId ?? null,
      constStartDate: startDate,
      constEndDate: endDate,
    });
  }

  if (isSiteLoading || isPeriodLoading || isWorklogChecking) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-text-secondary">불러오는 중…</p>
      </div>
    );
  }

  const dong = siteDetail?.addressDong ?? stepBResult.addressDong;
  const ho = siteDetail?.addressHo ?? stepBResult.addressHo;
  const addressLine = [
    siteDetail?.siteAddress ?? stepBResult.roadAddress,
    dong ? `${dong}동` : '',
    ho ? `${ho}호` : '',
  ]
    .filter(Boolean)
    .join(' ');

  // 사용자는 현장명을 먼저 인지 — 현장명 우선, 없으면 건물명으로 대체
  const siteName = siteDetail?.siteName || siteDetail?.bldgName || stepBResult.bldgName || '';

  // 기등록 공사현황의 진행상태에 맞춘 안내 문구.
  // DELAYED는 '지연'이 사용자에게 부정적으로 읽힐 수 있어 중립 문구로 대체.
  // 상태 단어만 치환 — DELAYED는 '지연' 대신 중립적인 '등록된'으로.
  const worklogStatusWord =
    activeWorklog?.progressStatus === 'READY' ? '준비중인'
    : activeWorklog?.progressStatus === 'IN_PROGRESS' ? '시공중인'
    : activeWorklog?.progressStatus === 'DELAYED' ? '등록된'
    : '진행중인';
  const worklogStatusNotice = `이 현장은 이미 ${worklogStatusWord} 공사현장입니다. 확인을 클릭하면 아래 현장으로 정보 조회됩니다.`;

  const periodLabel = startDate && endDate
    ? `${startDate} ~ ${endDate}`
    : startDate
      ? `${startDate} ~ 종료일을 선택하세요`
      : '달력에서 시작일, 종료일을 선택해주세요';


  return (
    <div className="space-y-4">

      {/* 이미 공사현황이 있는 현장 — 중복 등록 차단 (표준 인라인 안내 Alert) */}
      {isDuplicated && (
        <Alert kind="info">
          <p>
            {worklogStatusNotice}
            {schedulePeriod ? ' 인디플래너에 스케줄도 있어요.' : ''}
          </p>
          <p className="mt-3 truncate font-bold">{siteName || '현장명 없음'}</p>
          <p className="truncate text-caption">{addressLine || '주소 정보 없음'}</p>
        </Alert>
      )}

      {/* 공사기간 — 기획서 sm-period-trigger + sm-cal-wrap.
          달력이 컨트롤이라 Input 이 아니지만 라벨·.req 규격은 .field 구조를 따른다(D-09 §15.2) */}
      <div className="field field-auto">
        <label>
          공사기간 <span className="req" aria-hidden="true">*</span>
        </label>

        {/* 기간 요약 (읽기용) */}
        <div
          className={`flex w-full items-center gap-2 rounded-xxl border px-3 py-2.5 text-left text-sm ${
            !isReadOnly
              ? 'border-primary-500 bg-surface-subtle text-text-primary'
              : 'border-border-default bg-surface-raised text-text-muted'
          }`}
        >
          <svg className="shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span className="flex-1">{periodLabel}</span>
          {startDate && endDate && (
            <span className="shrink-0 text-sm font-bold text-primary-600">
              총 {countWorkdays(startDate, endDate, holidays)}일
            </span>
          )}
        </div>

        <div
          className={`mt-2.5 overflow-hidden rounded-xxl border border-border-default bg-surface-raised ${
            isReadOnly ? 'pointer-events-none opacity-60' : ''
          }`}
        >
            {/* 시작일 달력 · 종료일 달력 */}
            <div className="grid grid-cols-2 divide-x divide-border-default">
              <div>
                <p className="border-b border-border-default bg-surface-subtle py-1 text-center text-caption font-bold text-text-secondary">
                  시작일
                </p>
                <MonthCalendar
                  ym={startYm}
                  selected={startDate}
                  rangeStart={startDate}
                  rangeEnd={endDate}
                  disabled={isReadOnly}
                  onNav={(d) => setStartYm((ym) => shiftMonth(ym, d))}
                  onPick={handlePickStart}
                  holidays={holidays}
                />
              </div>
              <div>
                <p className="border-b border-border-default bg-surface-subtle py-1 text-center text-caption font-bold text-text-secondary">
                  종료일
                </p>
                <MonthCalendar
                  ym={endYm}
                  selected={endDate}
                  rangeStart={startDate}
                  rangeEnd={endDate}
                  minDate={startDate || undefined}
                  disabled={isReadOnly}
                  onNav={(d) => setEndYm((ym) => shiftMonth(ym, d))}
                  onPick={handlePickEnd}
                  holidays={holidays}
                />
              </div>
            </div>
          </div>

        {isScheduleLocked && !isDuplicated && (
          <div className="mt-1.5">
            <StatusHint
              tone="muted"
              icon={
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              }
            >
              인디플래너 공사스케줄에 등록된 기간입니다. 기간 변경은 인디플래너에서 해주세요.
            </StatusHint>
          </div>
        )}
      </div>

      {/* 하단 버튼 — onPrev 없으면(공사기간 단독 팝업) '이전' 자체를 숨긴다 */}
      <div className="flex gap-2 pt-1">
        {onPrev && (
          <Button variant="secondary" size="lg" type="button" onClick={onPrev} className="flex-none">
            ← 이전
          </Button>
        )}
        {/* 기등록 공사현장이면 신규 등록이 아니라 '확인'만 — 클릭 시 그 현장을 선택(현장패널 반영) */}
        <Button
          variant="primary"
          size="lg"
          type="button"
          onClick={isDuplicated ? () => onComplete(siteId, { selectedExisting: true }) : handleSave}
          disabled={isDuplicated ? false : !canSave || createWorklogMutation.isPending}
          className="flex-1"
        >
          {isDuplicated ? '확인' : createWorklogMutation.isPending ? '저장 중…' : '저장'}
        </Button>
      </div>
    </div>
  );
}
